import React, { useEffect, useState } from 'react';
import { Tabs, TabPanel } from 'react-tabs';
import Skeleton from 'react-loading-skeleton';

import { Button, LinkButton } from 'components/standalone';
import { Container, Flex } from 'styles';
import PurchasesService from 'services/PurchasesService';
import PurchaseModal from '../PurchaseModal/PurchaseModal';
import TicketCard from '../TicketCard/TicketCard';
import * as S from './styles';

interface PurchaseState {
	isOpen: boolean;
	isOrder: boolean;
	eventName: string;
	id: string;
	eventId: number | null;
	event: {
		id: number;
		name: string;
		startDate: string;
		customDate: string;
		fullAddress: string;
		locationType: string;
		thumbnail: string;
	} | null;
}

const TicketsContent = () => {
	const [ currentPurchase, setCurrentPurchase ] = useState<PurchaseState>({ isOpen: false, isOrder: false, eventName: '', id: '', eventId: null, event: null });
	const [ purchases, setPurchases ] = useState([]);
	const [ status, setStatus ] = useState({ isLoading: true, hasError: false, hasMore: false });

	useEffect(() => {
		getPurchases();
	}, [])

	const showTicketsModal = (purchase: any, isOrder: boolean) => {
		setCurrentPurchase({
			isOpen: true,
			isOrder,
			eventName: purchase.event.name,
			id: purchase.purchaseId,
			eventId: purchase.event.id,
			event: purchase.event,
		});
	}

	const closeTicketsModal = () => {
		setCurrentPurchase({ isOpen: false, isOrder: false, eventName: '', id: '', eventId: null, event: null });
	}

	const getPurchases = (isBuyer = true) => {
		const data = { eventStatuses: ['CANCELLED', 'FINISHED', 'PUBLISHED', 'UNPUBLISHED'], isBuyer };
		const params = { size: purchases.length + 20, sort: 'creationDate,DESC' };

		PurchasesService.getAllPurchases(data, params)
			.then(({ data }: any) => {
				setStatus({ isLoading: false, hasError: false, hasMore: !data.last })
				setPurchases(data.content)
			})
			.catch(() => setStatus({ isLoading: false, hasError: true, hasMore: false }))
	}

	const getSkeletonLayout = () => {
		if (status.isLoading && !status.hasError && !purchases.length) {
			return (
				<S.TicketsList>
					<Skeleton height={250} />
					<Skeleton height={250} />
					<Skeleton height={250} />
				</S.TicketsList>
			)
		}
	}

	const getErrorLayout = () => {
		if (status.hasError) {
			return (
				<S.TicketsFallback>
					<S.TicketsFallbackImage error />
					<S.TicketsFallbackTitle>Houve um erro ao buscar seus ingressos</S.TicketsFallbackTitle>
					<S.TicketsFallbackSubtitle>Caso o erro persista, entre em contato com o suporte</S.TicketsFallbackSubtitle>

					<Button handleClick={() => getPurchases()}>Tentar novamente</Button>
				</S.TicketsFallback>
			)
		}
	}

	const getFallbackLayout = () => {
		if (!status.isLoading && !status.hasError && !purchases.length) {
			return (
				<S.TicketsFallback>
					<S.TicketsFallbackImage />
					<S.TicketsFallbackTitle>Não há ingressos disponíveis</S.TicketsFallbackTitle>

					<LinkButton to="/eventos">Encontrar eventos</LinkButton>
				</S.TicketsFallback>
			)
		}
	}

	const getHasMoreLayout = () => {
		if (status.hasMore) {
			return (
				<Flex isWrap={false} justifyContent='center' marginBottom={20}>
					<Button handleClick={() => getPurchases()}>Carregar mais</Button>
				</Flex>
			)
		}
	}

	const setTabView = (index: number) => {
		purchases.length = 0;
		setStatus({ isLoading: true, hasError: false, hasMore: false });
		getPurchases(index === 0);
	}

	return (
		<Container marginBottom={50}>
			<Tabs defaultIndex={0} onSelect={index => setTabView(index)}>
				<S.CustomTabList>
					<S.CustomTab>Meus pedidos</S.CustomTab>
					<S.CustomTab>Ingressos relacionados a mim</S.CustomTab>
				</S.CustomTabList>

				<TabPanel>
					{getSkeletonLayout()}

					{getErrorLayout()}

					{getFallbackLayout()}

					<S.TicketsList>
						{purchases.map((purchase: any) => (
							<TicketCard
								key={purchase.purchaseId}
								ticket={purchase}
								isOrder
								seeTicketsFn={() => showTicketsModal(purchase, true)}
							/>
						))}
					</S.TicketsList>

					{getHasMoreLayout()}
				</TabPanel>

				<TabPanel>
					{getSkeletonLayout()}

					{getErrorLayout()}

					{getFallbackLayout()}

					<S.TicketsList>
						{purchases.map((purchase: any) => (
							<TicketCard
								key={purchase.purchaseId}
								ticket={purchase}
								isOrder={false}
								seeTicketsFn={() => showTicketsModal(purchase, false)}
							/>
						))}
					</S.TicketsList>

					{getHasMoreLayout()}
				</TabPanel>
			</Tabs>

			<PurchaseModal
				isOpen={currentPurchase.isOpen}
				isOrder={currentPurchase.isOrder}
				eventName={currentPurchase.eventName}
				id={currentPurchase.id}
				eventId={currentPurchase.eventId}
				event={currentPurchase.event}
				closeModalFn={() => closeTicketsModal()}
			/>
		</Container>
	);
}

export default TicketsContent;
